import { createQueryHook, type QueryHookRuntimeOptions } from '@/common/hooks/typedHookFactory';
import { QueryKeys } from '@/common/queryKeys';
import { graphql } from '@/generated/graphql';
import { POLLING_INTERVAL_MS } from '../constants';

const DELIVERED_NEW = { statusName: 'Delivered' };
const RESCHEDULE_NEW = { statusName: 'RescheduleOnDelivering' };
const RETURN_NEW = { statusName: 'ReturnOnDelivering' };

const GET_DRIVERS_WITH_LAST_TRACK_POINT = graphql(`
  query GetDriversWithLastTrackPoint(
    $dateFrom: timestamptz!
    $dateTo: timestamptz!
    $departmentId: Int!
    $deliveredNew: jsonb
    $rescheduleNew: jsonb
    $returnNew: jsonb
  ) {
    user(
      where: {
        roles: { _contains: "driver" }
        orders: { plannedDate: { _gte: $dateFrom, _lt: $dateTo } }
        departments_users: { departmentId: { _eq: $departmentId } }
      }
    ) {
      delivered: order_changes_aggregate(
        where: {
          createdAt: { _gt: $dateFrom, _lt: $dateTo }
          new: { _contains: $deliveredNew }
        }
      ) {
        aggregate {
          count
        }
      }
      rescheduled: order_changes_aggregate(
        where: {
          createdAt: { _gt: $dateFrom, _lt: $dateTo }
          new: { _contains: $rescheduleNew }
        }
      ) {
        aggregate {
          count
        }
      }
      returned: order_changes_aggregate(
        where: {
          createdAt: { _gt: $dateFrom, _lt: $dateTo }
          new: { _contains: $returnNew }
        }
      ) {
        aggregate {
          count
        }
      }
      name
      id
      email
      track_points(
        where: {
          _and: {
            geom: { _is_null: false }
            time: { _gte: $dateFrom, _lt: $dateTo }
          }
        }
        order_by: { time: desc }
        limit: 1
      ) {
        time
        lng
        lat
        id
        batteryLevel
      }
      orders(
        where: { plannedDate: { _gte: $dateFrom, _lt: $dateTo } }
        order_by: { plannedDate: desc }
      ) {
        id
        plannedDate
        plannedDateDuration
        createdAt
        statusName
        delivery_provider {
          short_name
          display_name
        }
      }
    }
  }
`);

const GET_DRIVERS_WITH_ADDRESS_BY_DATE = graphql(`
  query GetDriversWithAddressByDate(
    $id: String!
    $dateFrom: timestamptz!
    $dateTo: timestamptz!
    $departmentId: Int!
  ) {
    user(
      where: {
        id: { _eq: $id }
        roles: { _contains: "driver" }
        departments_users: { departmentId: { _eq: $departmentId } }
      }
    ) {
      id
      name
      email
      orders(
        where: { plannedDate: { _gte: $dateFrom, _lt: $dateTo } }
        order_by: { plannedDate: desc }
      ) {
        id
        shortCode
        plannedDate
        plannedDateDuration
        statusName
        totalCents
        createdAt
        client {
          id
          fullname
          address {
            city
            comments
            country
            district
            lat
            lng
            street
            state
            id
          }
        }
        positions {
          totalPrice
          quantity
          product {
            name
          }
        }
        delivery_provider {
          short_name
          display_name
        }
      }
    }
  }
`);

const GET_CLIENT_PHONE = graphql(`
  query GetClientPhone($clientId: Int!) {
    getClientPhone(clientId: $clientId) {
      phone
    }
  }
`);

type GetDriversWithLastTrackPointVariables = {
  dateFrom: string;
  dateTo: string;
  departmentId: number;
};

const jsonbVariables = {
  deliveredNew: DELIVERED_NEW,
  rescheduleNew: RESCHEDULE_NEW,
  returnNew: RETURN_NEW,
};

const GET_DRIVER_TRACK_POINTS = graphql(`
  query GetDriverTrackPoints(
    $driverId: String!
    $dateFrom: timestamptz!
    $dateTo: timestamptz!
  ) {
    track_point(
      where: {
        userID: { _eq: $driverId }
        time: { _gte: $dateFrom, _lt: $dateTo }
        geom: { _is_null: false }
      }
      order_by: { time: asc }
    ) {
      id
      lat
      lng
      time
    }
  }
`);

const GET_DRIVER_ORDER_CHANGES = graphql(`
  query GetDriverOrderChanges(
    $driverId: String!
    $dateFrom: timestamptz!
    $dateTo: timestamptz!
  ) {
    order_change(
      where: {
        editorId: { _eq: $driverId }
        createdAt: { _gte: $dateFrom, _lt: $dateTo }
        new: { _has_key: "statusName" }
      }
      order_by: { createdAt: asc }
    ) {
      id
      orderId
      old
      new
      createdAt
      geo_event {
        id
        lat
        lng
        createdAt
      }
    }
  }
`);

const useGetDriversWithLastTrackPointBase = createQueryHook(
  GET_DRIVERS_WITH_LAST_TRACK_POINT,
  (variables: GetDriversWithLastTrackPointVariables) => [
    QueryKeys.GET_DRIVERS_WITH_LAST_TRACK_POINT,
    variables.departmentId,
    variables.dateFrom,
    variables.dateTo,
  ],
  {
    refetchInterval: POLLING_INTERVAL_MS,
  }
);

export const useGetDriversWithLastTrackPoint = (
  variables: GetDriversWithLastTrackPointVariables,
  runtimeOptions?: QueryHookRuntimeOptions
) => useGetDriversWithLastTrackPointBase({ ...variables, ...jsonbVariables }, runtimeOptions);

export const useGetDriversWithAddressByDate = createQueryHook(
  GET_DRIVERS_WITH_ADDRESS_BY_DATE,
  (variables) => [
    QueryKeys.GET_DRIVERS_WITH_ADDRESS_BY_DATE,
    variables.id,
    variables.departmentId,
    variables.dateFrom,
    variables.dateTo,
  ],
  {
    enabled: (variables) => Boolean(variables.id),
    refetchInterval: POLLING_INTERVAL_MS,
  }
);

export const useGetClientPhone = createQueryHook(
  GET_CLIENT_PHONE,
  (variables) => [QueryKeys.GET_CLIENT_PHONE, variables.clientId],
  {
    enabled: (variables) => Boolean(variables.clientId),
  }
);

export const useGetDriverTrackPoints = createQueryHook(
  GET_DRIVER_TRACK_POINTS,
  (variables) => [
    QueryKeys.GET_DRIVER_TRACK_POINTS,
    variables.driverId,
    variables.dateFrom,
    variables.dateTo,
  ],
  {
    enabled: (variables) => Boolean(variables.driverId),
    refetchInterval: POLLING_INTERVAL_MS,
  }
);

export const useGetDriverOrderChanges = createQueryHook(
  GET_DRIVER_ORDER_CHANGES,
  (variables) => [
    QueryKeys.GET_DRIVER_ORDER_CHANGES,
    variables.driverId,
    variables.dateFrom,
    variables.dateTo,
  ],
  {
    enabled: (variables) => Boolean(variables.driverId),
    refetchInterval: POLLING_INTERVAL_MS,
  }
);
